import React, { useState } from 'react';
import { useUsers, useUserLimitStatus } from '../hooks/useUsers';
import { usePermissions } from '../hooks/usePermissions';
import { Button } from '../components/ui/button';
import { Plus } from 'lucide-react';
import UserList from '../components/users/UserList';
import UserForm from '../components/users/UserForm';
import UserLimitWarning from '../components/users/UserLimitWarning';
import LoadingSpinner from '../components/common/LoadingSpinner';
import type { User } from '../types/user';

const Users: React.FC = () => {
  const { canManageUsers } = usePermissions();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active');

  const { data: users, isLoading, error } = useUsers({
    activeOnly: filterActive === 'active',
  });

  const { data: limitStatus } = useUserLimitStatus();

  if (!canManageUsers) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-yellow-800 mb-2">Acceso Restringido</h2>
        <p className="text-yellow-700">
          Solo los administradores pueden gestionar usuarios.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
        <p className="text-red-700">
          No se pudieron cargar los usuarios. Por favor, intenta de nuevo.
        </p>
      </div>
    );
  }

  const filteredUsers = Array.isArray(users) ? users.filter((user) => {
    if (filterActive === 'active') return user.isActive;
    if (filterActive === 'inactive') return !user.isActive;
    return true;
  }) : [];

  const handleCreateClick = () => {
    setEditingUser(null);
    setShowCreateForm(true);
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setShowCreateForm(true);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setEditingUser(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usuarios</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestiona los usuarios de tu empresa
          </p>
        </div>
        <Button
          onClick={handleCreateClick}
          disabled={limitStatus?.isAtLimit}
          size="default"
        >
          <Plus className="w-4 h-4 mr-2" />
          Crear Usuario
        </Button>
      </div>

      {/* User Limit Warning */}
      {limitStatus && <UserLimitWarning status={limitStatus} />}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrar:</label>
          <div className="flex gap-2">
            <Button
              variant={filterActive === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive('all')}
            >
              Todos
            </Button>
            <Button
              variant={filterActive === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive('active')}
            >
              Activos
            </Button>
            <Button
              variant={filterActive === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterActive('inactive')}
            >
              Inactivos
            </Button>
          </div>
          <div className="ml-auto text-sm text-gray-600">
            {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* User List */}
      <UserList users={filteredUsers} onEdit={handleEditClick} />

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <UserForm
          user={editingUser}
          open={showCreateForm}
          onClose={handleCloseForm}
          limitStatus={limitStatus}
        />
      )}
    </div>
  );
};

export default Users;
